import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  inject,
  input,
  InputSignal,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Store } from '@ngxs/store';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import { filter, finalize, map, startWith, switchMap, tap } from 'rxjs/operators';

import { BaseCommonClientRequest, CommonClientRequestsState } from '@aiwyn/common/client-requests';

import {
  ClientRequestApiService,
  ClientRequestDocumentUploadModel,
  ClientRequestItemFileReference,
  ClientRequestItemStatus,
  RequestDocument,
  RequestItem,
  RequestItemType,
} from '@aiwyn/api';
import { LaunchDarklyFlag, LaunchDarklyService } from '@aiwyn/config';
import { FilePreviewService } from '@aiwyn/services';
import {
  Avatar,
  DATA_SELECTOR_INJECTION_TOKEN,
  DEFAULT_SNACKBAR_CONFIG,
  getAvatarByFullName,
  ignoreNullish,
  SnackbarComponent,
  SnackbarType,
} from '@aiwyn/shared';

import {
  ConfirmationModalComponent,
  ConfirmationPayload,
} from 'src/app/shared/containers/dialog/confirmation/confirmation-modal.component';
import { DocumentRequestModalComponent, DocumentRequestModalData } from '../../../../../components';
import { ClientRequestsState, SetSelectedRequestItemId, UpdateSecureLinkGrants } from '../../../../../state';
import { ReassignModalComponent, ReassignModalData } from './components/reassign-modal/reassign-modal.component';
import { buildReferenceFilePreviewList } from '../../client-request-panel.utils';
import { toSignal } from '@angular/core/rxjs-interop';
import { CommentsSelectors } from '../../../../../components/comments/state/comments.selectors';

@Component({
  selector: 'awn-request-item-card',
  templateUrl: './request-item-card.component.html',
  styleUrl: './request-item-card.component.scss',
  providers: [
    {
      provide: DATA_SELECTOR_INJECTION_TOKEN,
      useValue: 'request-item-card',
    },
  ],
  standalone: false,
})
export class RequestItemCardComponent implements OnChanges {
  @ViewChild('requestItemElement') requestItemElement: ElementRef<HTMLElement>;
  public dataSelector = inject(DATA_SELECTOR_INJECTION_TOKEN);

  instance: InputSignal<BaseCommonClientRequest<ClientRequestDocumentUploadModel>> = input();
  requestItem: InputSignal<RequestItem> = input();
  sectionReferenceId: InputSignal<string | undefined> = input();

  readonly: InputSignal<boolean> = input();

  @Output() showRequestActivity = new EventEmitter<void>();

  commentsState = this._store.selectSignal(CommentsSelectors.state);

  isReadyToSubmit = computed(() => this.requestItem()?.isReadyToSubmit ?? false)

  // LaunchDarkly flag for IN_REVIEW items editing
  protected isInReviewEditingEnabled = toSignal(inject(LaunchDarklyService).getFeatureFlag(LaunchDarklyFlag.ENABLE_IN_REVIEW_ITEMS_EDITING), {
    initialValue: false,
  });

  shouldShowReadyToSubmitBadge = computed(() => {
    const item = this.requestItem();
    const isInReviewEditingEnabled = this.isInReviewEditingEnabled();

    if (isInReviewEditingEnabled) {
      // New behavior: Show badge for all except DONE
      return item?.status !== ClientRequestItemStatus.DONE;
    } else {
      // Original behavior: Show badge for all except DONE and IN_REVIEW
      return item?.status !== ClientRequestItemStatus.DONE && item?.status !== ClientRequestItemStatus.IN_REVIEW;
    }
  });

  readyToSubmitBadgeLabel = computed(() => {
    return 'Ready to Submit';
  });

  unreadMessagesCount = computed(() => {
    const item = this.requestItem();
    return CommentsSelectors.unreadCountByEntity(this.commentsState(), item?.id);
  });

  protected readonly enableCommentsInActivitySidebar = toSignal(
    this._launchDarklyService.getFeatureFlag(LaunchDarklyFlag.ENABLE_CLIENT_REQUEST_THREADED_COMMENTS),
  );

  requestItem$: BehaviorSubject<RequestItem> = new BehaviorSubject<RequestItem>(null);

  protected isDeleting = false;
  protected showMoreReferenceFilesButton = signal(true);
  protected readonly MAX_VISIBLE_FILES = 5;

  protected visibleReferenceFiles = computed(() => {
    const files = this.requestItem()?.clientRequestItemFileReferences || [];
    return this.showMoreReferenceFilesButton() ? files.slice(0, this.MAX_VISIBLE_FILES) : files;
  });

  protected remainingFilesCount = computed(() => {
    const files = this.requestItem()?.clientRequestItemFileReferences || [];
    return Math.max(0, files.length - this.MAX_VISIBLE_FILES);
  });

  avatars$: Observable<Avatar[]> = combineLatest([
    this.requestItem$,
    this._store.select(CommonClientRequestsState.getClientRequestDetailsLoadable),
  ]).pipe(
    ignoreNullish(),
    map(([requestItem]) => {
      return requestItem.assignees.flatMap((_assignee) => {
        const secureLinkUsers = _assignee.secureLinkUsers;

        const mappedSecureLinkUsers = secureLinkUsers.map((secureLinkUser) =>
          getAvatarByFullName(secureLinkUser.uuid, secureLinkUser.displayName, '(Guest)'),
        );

        const assignee = this._store.selectSnapshot(CommonClientRequestsState.getAssigneeLoadableById(_assignee.contactId)).data;
        const fullName = assignee.contactInfo.fullName;

        return secureLinkUsers.length ? mappedSecureLinkUsers : [getAvatarByFullName(assignee.id, fullName)];
      });
    }),
    startWith([]),
  );

  isActive$: Observable<boolean> = this._store.select(ClientRequestsState.getSlices.selectedRequestItemId).pipe(
    map((requestItemId) => requestItemId === this.requestItem().id),
    tap((isActive) => this.centerScrollItemCard(isActive)),
  );

  protected enableReferenceFiles$ = inject(LaunchDarklyService).getFeatureFlag(LaunchDarklyFlag.ENABLE_REFERENCE_FILES);
  protected enableReferenceId$ = inject(LaunchDarklyService).getFeatureFlag(LaunchDarklyFlag.ENABLE_REFERENCE_ID);

  protected itemReferenceId = computed(() => {
    const sectionRef = this.sectionReferenceId();
    const itemRef = this.requestItem()?.referenceId;

    if (sectionRef && itemRef) {
      return `${sectionRef}.${itemRef}`;
    }
    return itemRef || null;
  });

  constructor(
    private readonly _clientRequestApiService: ClientRequestApiService,
    private readonly _dialog: MatDialog,
    private readonly _store: Store,
    private readonly _snackbar: MatSnackBar,
    private readonly _filePreviewService: FilePreviewService,
    private readonly _launchDarklyService: LaunchDarklyService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    const { requestItem } = changes;

    if (requestItem) {
      this.requestItem$.next(requestItem.currentValue);
    }
  }

  selectRequestItem(): void {
    this._store.dispatch(new SetSelectedRequestItemId(this.requestItem().id));
  }

  onShowRequestComments(): void {
    // First select the request item to ensure it's active
    this.selectRequestItem();
    // Then emit event to force sidebar open
    this.showRequestActivity.emit();
  }

  openLinkFilesDialog(): void {
    if (this.readonly() || this.requestItem().status === ClientRequestItemStatus.DONE) {
      return;
    }

    this._dialog.open<DocumentRequestModalComponent, DocumentRequestModalData>(DocumentRequestModalComponent, {
      data: {
        requestDocument: this.requestItem() as RequestDocument,
        instance: this.instance(),
        notApplicableItemIds: new Set(this._store.selectSnapshot(ClientRequestsState.getNotApplicableItemsForSelectedRequest)),
      },
    });
  }

  updateAssignee(e: MouseEvent, avatar?: Avatar): void {
    e?.stopPropagation();
    e?.preventDefault();

    if (this.requestItem().status !== ClientRequestItemStatus.OPEN) {
      return;
    }

    if (this.requestItem().assignees[0]?.secureLinkUsers?.length) {
      this._dialog
        .open<ConfirmationModalComponent, ConfirmationPayload>(ConfirmationModalComponent, {
          data: {
            title: 'Undo Reassignment',
            message: 'Are you sure you want to undo the reassignment for this request item?',
            confirmButtonText: 'Undo',
            confirmButtonColor: 'negative',
          },
        })
        .afterClosed()
        .pipe(
          filter(Boolean),
          tap((result) => {
            this.removeSecureLink();
          }),
        )
        .subscribe();

      return;
    }

    let secureLinkUser = this.requestItem().assignees[0]?.secureLinkUsers?.[0];

    if (avatar) {
      secureLinkUser = this.requestItem().assignees[0].secureLinkUsers.find((secLinkUser) => secLinkUser.uuid === avatar.id);
    }

    this._dialog.open<ReassignModalComponent, ReassignModalData>(ReassignModalComponent, {
      data: {
        requestDocument: this.requestItem() as RequestDocument,
        requestName$: this.instance().name$.pipe(filter(Boolean)),
        rawSections$: this.instance().typeClass$.pipe(
          filter(Boolean),
          switchMap((typeClass) => typeClass.sections$),
        ),
        secureLinkUser,
      },
    });
  }

  removeSecureLink(): void {
    this.isDeleting = true;

    this._clientRequestApiService
      .getSecureLinksByUUID(this.requestItem().assignees[0].secureLinkUsers[0].uuid)
      .pipe(
        switchMap((secureLink) => {
          return this._store.dispatch(
            new UpdateSecureLinkGrants({
              UUID: this.requestItem().assignees[0].secureLinkUsers[0].uuid,
              grants: secureLink
                .filter((grant) => grant.active)
                .map((grant) =>
                  grant.active && this.requestItem().assignees.some((assignee) => assignee.id === grant.entityId)
                    ? {
                        ...grant,
                        active: false,
                      }
                    : grant,
                ),
              clientId: this._store.selectSnapshot(ClientRequestsState.getSelectedClientRequest).clientId,
              message: '',
              secureLinkUser: this.requestItem().assignees[0].secureLinkUsers[0],
            }),
          );
        }),
        tap(() => {
          this._snackbar.openFromComponent(SnackbarComponent, {
            ...DEFAULT_SNACKBAR_CONFIG,
            data: {
              message: 'Secure link successfully removed for request item',
              type: SnackbarType.SUCCESS,
            },
          });
        }),
        finalize(() => {
          this.isDeleting = false;
        }),
      )
      .subscribe();
  }

  onReferenceFileClick(fileRef: ClientRequestItemFileReference, event: MouseEvent): void {
    event.stopPropagation();

    // Sidebar shows the item's full reference list, not just the visible slice
    const allReferences = this.requestItem()?.clientRequestItemFileReferences ?? [];
    const { files, initialIndex } = buildReferenceFilePreviewList(allReferences, fileRef);
    this._filePreviewService.previewMany(files, initialIndex);
  }

  expandReferenceFiles(event: MouseEvent): void {
    event.stopPropagation();
    this.showMoreReferenceFilesButton.set(false);
  }

  centerScrollItemCard(isActive: boolean): void{
    if (isActive) {
      const element = this.requestItemElement.nativeElement;
      const scrollContainer = element.closest('.client-request-content-scrollable') as HTMLElement;

      if (!scrollContainer) {
        return;
      }

      // Calculate position to center the element in the viewport
      const elementTop = element.offsetTop;
      const elementHeight = element.offsetHeight;
      const containerHeight = scrollContainer.clientHeight;

      // Center position: element's top - (viewport height - element height) / 2
      const targetScrollTop = elementTop - (containerHeight - elementHeight) / 2;

      scrollContainer.scrollTo({
        top: targetScrollTop,
        behavior: 'smooth'
      });
    }
  }

  protected readonly ClientRequestItemStatus = ClientRequestItemStatus;
  protected readonly RequestItemType = RequestItemType;
}
